import * as THREE from 'three'
import { FontLoader } from 'three/addons/loaders/FontLoader.js'
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import GUI from 'lil-gui'
import gsap from 'gsap'

// "Fábrica": traduce un nombre (el texto que viene del HTML) a una geometría.
function crearGeometria(tipo) {
    switch (tipo) {
        // Sólidos básicos
        case 'cubo':     return new THREE.BoxGeometry(1.6, 1.6, 1.6)
        case 'esfera':   return new THREE.SphereGeometry(1.1, 32, 32)
        case 'cilindro': return new THREE.CylinderGeometry(0.9, 0.9, 2, 32)
        case 'cono':     return new THREE.ConeGeometry(1, 2, 32)
        case 'capsula':  return new THREE.CapsuleGeometry(0.7, 1, 8, 16)
        // Toros / nudos
        case 'nudo':     return new THREE.TorusKnotGeometry(0.8, 0.35, 100, 16)
        // Poliedros facetados (look "cristal")
        case 'diamante': return new THREE.OctahedronGeometry(1.3, 0)
        case 'cristal':  return new THREE.IcosahedronGeometry(1.2, 0)
        case 'gema':     return new THREE.DodecahedronGeometry(1.2, 0)
        case 'toro':
        default:         return new THREE.TorusGeometry(1, 0.4, 16, 60)
    }
}

// Resiliencia: ¿el navegador/dispositivo soporta WebGL?
function webglDisponible() {
    try {
        const lienzo = document.createElement('canvas')
        return !!(window.WebGLRenderingContext &&
            (lienzo.getContext('webgl') || lienzo.getContext('experimental-webgl')))
    } catch (e) {
        return false
    }
}

/**
 * Inicializa una escena de portafolio 3D sobre un <canvas>.
 *
 * Lo PER-SECCIÓN (color, figura, modelo, texto) viene del HTML por data-*
 * (eso lo imprimirá tu PHP, distinto para cada portafolio).
 * Lo GLOBAL de la escena viene por `opciones`.
 *
 * @param {object} opciones
 * @returns {{ destroy: () => void }} controlador para limpiar la escena.
 */
export function initPortafolio3D(opciones = {}) {
    // Opciones globales con valores por defecto. Cualquiera se puede sobreescribir.
    const config = {
        canvas: null,                                       // <canvas> destino (obligatorio)
        selectorSecciones: '.section',                      // de dónde leer las secciones
        rutaModelos: '/models/',                            // carpeta de los .glb
        rutaFuente: '/fonts/helvetiker_regular.typeface.json',
        rutaGradiente: '/textures/gradients/3.jpg',
        colorDefecto: '#ffeded',                            // color si una sección no trae data-color
        colorFondo: null,                                   // color de fondo del canvas (null = negro por defecto)
        colorParticulas: null,                              // color de las partículas (null = usa colorDefecto; oscuro para fondo claro)
        colorAcento: null,                                  // color de las "chispas" de marca (magenta); null = todas iguales
        proporcionAcento: 0,                                // fracción de partículas en colorAcento (0–1); úsalo bajo (~.1) para que destelle sin saturar
        sombraModelos: false,                               // sombra suave detrás de cada modelo (aterriza + separa del fondo claro)
        distanciaSecciones: 4,
        separacionModelo: 1.3,                              // distancia horizontal del modelo al centro (menor = más cerca del texto)
        cantidadParticulas: 200,
        intensidadEntorno: 0.35,                            // fuerza de los reflejos de estudio (1 = full; bajo para no ahogar pantallas emisivas)
        usarBloom: true,                                    // post-procesado glow (PESADO: apagar en móvil/tablet)
        pixelRatioMax: 2,                                   // tope de densidad de píxeles (1 en móvil = mucho más rápido)
        apilado: false,                                     // móvil: modelo centrado y abajo (texto arriba) en vez de a un lado
        movil: false,                                       // usa data-src-movil (modelos ultra-ligeros) si existe
        escalaFactor: 1,                                    // multiplica el tamaño de los modelos (móvil < 1 para que no se desborden)
        exposicion: 1.0,                                    // exposición global (sube en móvil: sin bloom la escena se ve más oscura)
        tamañoParticulas: 0.03,                             // tamaño de las "estrellas" (sube en móvil para que se perciban)
        intensidadLuz: 1,                                   // multiplica las luces (sube en móvil, que no tiene bloom, para más brillo)
        bloomFuerza: 0.2,                                   // intensidad del glow
        bloomRadio: 0.4,                                    // qué tanto se difumina el glow
        bloomUmbral: 0.9,                                   // brillo mínimo para "brillar" (alto = solo lo emisivo; bajo = todo brilla)
        debug: false,                                       // muestra el panel GUI (solo en desarrollo)
        ...opciones,
    }

    const canvas = config.canvas
    if (!canvas) {
        console.warn('initPortafolio3D: falta el canvas en las opciones.')
        return { destroy() {} }
    }

    // Resiliencia: sin WebGL no rompemos nada. Ocultamos el canvas y dejamos
    // el contenido HTML (textos) visible y usable.
    if (!webglDisponible()) {
        console.warn('WebGL no disponible: se muestra el contenido sin escena 3D.')
        canvas.style.display = 'none'
        const pre = document.getElementById('preloader')
        if (pre) pre.style.display = 'none' // sin 3D no hay carga: no dejar el preloader pegado
        return { destroy() {} }
    }

    // Accesibilidad: respeta a quien activó "reducir movimiento" en su sistema.
    const movimientoReducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const objectsDistance = config.distanciaSecciones
    const parameters = { materialColor: config.colorDefecto }
    const gui = config.debug ? new GUI() : null

    const scene = new THREE.Scene()

    // Gestor de carga: rastrea el progreso de TODOS los assets (textura, fuente,
    // modelos) para alimentar el preloader y disparar la "revelación" al terminar.
    const manager = new THREE.LoadingManager()

    // Textura de gradiente para el look "toon"
    const textureLoader = new THREE.TextureLoader(manager)
    const gradientTexture = textureLoader.load(config.rutaGradiente)
    gradientTexture.magFilter = THREE.NearestFilter

    /**
     * Secciones (data-driven): leemos lo que el HTML/PHP declaró.
     */
    const sectionEls = [...document.querySelectorAll(config.selectorSecciones)]
    const sectionMeshes = []      // alineado por índice con las secciones (puede tener null temporal)
    const sectionMaterials = []
    const titulos3D = []          // títulos de sección como texto 3D (decorativos)

    sectionEls.forEach((section, i) => {
        const material = new THREE.MeshToonMaterial({
            color: section.dataset.color || parameters.materialColor,
            gradientMap: gradientTexture,
        })
        sectionMaterials[i] = material

        // Texto 3D y modelos GLTF se construyen aparte (cargan asíncronos).
        if (section.dataset.figura === 'texto' || section.dataset.figura === 'modelo') {
            sectionMeshes[i] = null
            return
        }

        const mesh = new THREE.Mesh(crearGeometria(section.dataset.figura), material)
        mesh.position.y = (- objectsDistance * i) + (config.apilado ? -1.15 : 0)
        mesh.position.x = config.apilado ? 0 : (i % 2 === 0 ? config.separacionModelo : -config.separacionModelo)
        scene.add(mesh)
        sectionMeshes[i] = mesh
    })

    /**
     * Texto 3D (data-figura="texto") y títulos 3D (data-titulo3d).
     * Comparten una sola carga de fuente (asíncrona).
     */
    const fontLoader = new FontLoader(manager)
    fontLoader.load(config.rutaFuente, (font) => {
        // Texto 3D "héroe": data-figura="texto" + data-texto
        sectionEls.forEach((section, i) => {
            if (section.dataset.figura !== 'texto') return

            const geometry = new TextGeometry(section.dataset.texto || 'Texto', {
                font, size: 0.6, depth: 0.2, curveSegments: 5,
                bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.02, bevelSegments: 4,
            })
            geometry.center()

            const mesh = new THREE.Mesh(geometry, sectionMaterials[i])
            mesh.position.y = - objectsDistance * i
            mesh.userData.esTexto = true
            scene.add(mesh)
            sectionMeshes[i] = mesh
        })

        // Títulos 3D: encabezado (h1..h6) convertido en letras 3D.
        // El encabezado HTML se oculta a la vista pero permanece para SEO/accesibilidad.
        sectionEls.forEach((section, i) => {
            if (section.dataset.titulo3d === undefined) return

            const encabezado = section.querySelector('h1, h2, h3, h4, h5, h6')
            if (!encabezado) return

            const geometry = new TextGeometry(encabezado.textContent.trim(), {
                font, size: parseFloat(section.dataset.tituloEscala) || 0.55, depth: 0.15,
                curveSegments: 5, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.015, bevelSegments: 3,
            })
            geometry.center()

            const material = new THREE.MeshToonMaterial({
                color: section.dataset.color || parameters.materialColor,
                gradientMap: gradientTexture,
            })
            const titulo = new THREE.Mesh(geometry, material)
            titulo.position.y = - objectsDistance * i + 1.3
            titulo.userData.baseY = titulo.position.y
            titulo.userData.flotaOffset = i * 1.3
            scene.add(titulo)
            titulos3D.push(titulo)

            encabezado.classList.add('sr-only')
        })
    })

    /**
     * Modelos GLTF (data-figura="modelo" + data-src). Carga asíncrona.
     */
    // Sombra suave para los modelos (solo si se pide, p. ej. modo claro). Textura
    // radial generada por código (cero archivos) reutilizada por todos.
    let matSombra = null
    if (config.sombraModelos) {
        const lienzo = document.createElement('canvas')
        lienzo.width = lienzo.height = 128
        const ctx = lienzo.getContext('2d')
        const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
        g.addColorStop(0, 'rgba(0, 0, 0, 0.32)')
        g.addColorStop(0.55, 'rgba(0, 0, 0, 0.12)')
        g.addColorStop(1, 'rgba(0, 0, 0, 0)')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, 128, 128)
        matSombra = new THREE.SpriteMaterial({
            map: new THREE.CanvasTexture(lienzo),
            transparent: true,
            depthWrite: false,
        })
    }

    const gltfLoader = new GLTFLoader(manager)
    gltfLoader.setMeshoptDecoder(MeshoptDecoder) // necesario para los modelos móviles (meshopt)
    sectionEls.forEach((section, i) => {
        if (section.dataset.figura !== 'modelo' || !section.dataset.src) return

        const tieneTitulo = section.dataset.titulo3d !== undefined
        // En móvil usa la variante ultra-ligera si la sección la declara.
        const archivo = (config.movil && section.dataset.srcMovil) ? section.dataset.srcMovil : section.dataset.src

        gltfLoader.load(
            config.rutaModelos + archivo,
            (gltf) => {
                const modelo = gltf.scene

                // Normalizar tamaño (cada modelo viene en su propia escala).
                const escalaDeseada = (parseFloat(section.dataset.escala) || (tieneTitulo ? 2.2 : 3)) * config.escalaFactor
                const caja = new THREE.Box3().setFromObject(modelo)
                const tam = caja.getSize(new THREE.Vector3())
                modelo.scale.setScalar(escalaDeseada / Math.max(tam.x, tam.y, tam.z))

                // Recentrar en su origen.
                const cajaEscalada = new THREE.Box3().setFromObject(modelo)
                modelo.position.sub(cajaEscalada.getCenter(new THREE.Vector3()))

                const grupo = new THREE.Group()
                grupo.add(modelo)
                // Apilado (móvil): centrado y abajo, debajo del texto. Con título 3D:
                // centrado. Si no: a un lado (alterna izquierda/derecha).
                grupo.position.x = (tieneTitulo || config.apilado) ? 0 : (i % 2 === 0 ? config.separacionModelo : -config.separacionModelo)
                grupo.position.y = (- objectsDistance * i) + (tieneTitulo ? -0.6 : 0) + (config.apilado ? -1.15 : 0)
                grupo.rotation.y = THREE.MathUtils.degToRad(parseFloat(section.dataset.giro) || 0)

                grupo.userData.esModelo = true
                grupo.userData.baseY = grupo.position.y
                grupo.userData.flotaOffset = i * 1.7

                scene.add(grupo)
                sectionMeshes[i] = grupo

                // Sombra suave detrás del modelo (aterriza + lo separa del fondo
                // claro). Es un sprite (siempre mira a cámara), estático en la escena.
                if (matSombra) {
                    const sombra = new THREE.Sprite(matSombra)
                    sombra.scale.setScalar(escalaDeseada * 1.8)
                    sombra.position.set(
                        grupo.position.x,
                        grupo.position.y - escalaDeseada * 0.1,
                        grupo.position.z - escalaDeseada * 0.5,
                    )
                    scene.add(sombra)
                }

                precargarEnGPU(grupo) // sube el modelo a la GPU YA: evita el tirón al entrar en cámara
            },
            undefined,
            (error) => console.error(`No se pudo cargar el modelo "${section.dataset.src}":`, error),
        )
    })

    /**
     * Partículas (su rango vertical se adapta al número de secciones).
     */
    const cantidad = config.cantidadParticulas
    const positions = new Float32Array(cantidad * 3)
    for (let i = 0; i < cantidad; i++) {
        positions[i * 3 + 0] = (Math.random() - 0.5) * 10
        positions[i * 3 + 1] = objectsDistance * 0.5 - Math.random() * objectsDistance * sectionEls.length
        positions[i * 3 + 2] = (Math.random() - 0.5) * 10
    }
    const particlesGeometry = new THREE.BufferGeometry()
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

    const colorBase = new THREE.Color(config.colorParticulas || parameters.materialColor)
    const opcionesMaterial = { sizeAttenuation: true, size: config.tamañoParticulas }

    // Chispas de marca: un % de partículas en color acento (magenta). Se hace con
    // COLOR POR VÉRTICE (atributo 'color' + vertexColors), así sigue siendo UN solo
    // objeto Points y UNA draw call (cero costo extra). El resto va en color base.
    if (config.colorAcento && config.proporcionAcento > 0) {
        const colorAcento = new THREE.Color(config.colorAcento)
        const colores = new Float32Array(cantidad * 3)
        for (let i = 0; i < cantidad; i++) {
            const c = Math.random() < config.proporcionAcento ? colorAcento : colorBase
            colores[i * 3 + 0] = c.r
            colores[i * 3 + 1] = c.g
            colores[i * 3 + 2] = c.b
        }
        particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colores, 3))
        opcionesMaterial.vertexColors = true // el color sale del atributo, no de material.color
    } else {
        opcionesMaterial.color = colorBase
    }

    const particlesMaterial = new THREE.PointsMaterial(opcionesMaterial)
    const particles = new THREE.Points(particlesGeometry, particlesMaterial)
    scene.add(particles)

    // El panel de debug (si está activo) controla el color global.
    if (gui) {
        gui.addColor(parameters, 'materialColor').onChange(() => {
            sectionMaterials.forEach((m) => m.color.set(parameters.materialColor))
            particlesMaterial.color.set(parameters.materialColor)
        })
    }

    /**
     * Luces (la hemisférica de relleno evita que los modelos PBR se vean oscuros).
     */
    const directionalLight = new THREE.DirectionalLight('#ffffff', 1 * config.intensidadLuz)
    directionalLight.position.set(1, 1, 0)
    scene.add(directionalLight)
    const hemiLight = new THREE.HemisphereLight('#ffffff', '#404060', 1.2 * config.intensidadLuz)
    scene.add(hemiLight)

    /**
     * Tamaños, cámara y renderer.
     */
    const sizes = { width: window.innerWidth, height: window.innerHeight }

    const cameraGroup = new THREE.Group()
    scene.add(cameraGroup)
    const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
    camera.position.z = 8 // arranca alejada; la revelación hace dolly-in hasta 6
    cameraGroup.add(camera)

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, config.pixelRatioMax))
    if (config.colorFondo) renderer.setClearColor(config.colorFondo)

    // --- Calidad de imagen (esto es lo que "des-cartooniza" los modelos) ---
    // 1) Tone mapping ACES: mapea el rango de luz HDR a la pantalla con una
    //    curva cinematográfica (sombras y altas luces creíbles, no "lavadas").
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = config.exposicion // sube/baja la exposición global
    // (el espacio de color de salida ya es sRGB por defecto en three r152+)

    // 2) Environment map (IBL): un entorno de estudio que los materiales PBR
    //    usan para REFLEJAR luz. Es lo que da el look "metal/vidrio real".
    //    RoomEnvironment se genera por código: cero archivos que descargar.
    const pmrem = new THREE.PMREMGenerator(renderer)
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
    // Intensidad moderada: suficiente reflejo para el look "metal/vidrio real",
    // pero sin ahogar las pantallas emisivas (la del laptop "encendida").
    scene.environmentIntensity = config.intensidadEntorno
    pmrem.dispose() // ya tenemos la textura; liberamos el generador

    /**
     * Post-procesado: BLOOM (glow). Hace "brillar" lo emisivo (pantallas,
     * teclado, charts) y sobre todo el acento magenta. Es el efecto que más
     * eleva un portafolio 3D. En vez de pintar directo a pantalla, renderizamos
     * por una cadena de pasos (EffectComposer).
     */
    // En móvil/tablet apagamos el bloom (config.usarBloom = false): el post-procesado
    // es lo más pesado y tumba el rendimiento. Sin composer renderizamos directo,
    // y el tone mapping lo aplica el propio renderer.
    let composer = null
    if (config.usarBloom) {
        composer = new EffectComposer(renderer)
        composer.setSize(sizes.width, sizes.height)
        composer.setPixelRatio(Math.min(window.devicePixelRatio, config.pixelRatioMax))
        composer.addPass(new RenderPass(scene, camera))
        const bloomPass = new UnrealBloomPass(
            new THREE.Vector2(sizes.width, sizes.height),
            config.bloomFuerza,
            config.bloomRadio,
            config.bloomUmbral,
        )
        composer.addPass(bloomPass)
        // OutputPass aplica el tone mapping (ACES) y el espacio de color AL FINAL de
        // la cadena; así el bloom trabaja en luz lineal (HDR), como debe ser.
        composer.addPass(new OutputPass())
    }

    // Pre-sube un objeto a la GPU (geometría + shaders) renderizándolo una vez con
    // el culling apagado. Así un modelo pesado NO "se renderiza de golpe" (tirón)
    // la primera vez que entra en cámara al hacer scroll: el coste se paga al cargar.
    function precargarEnGPU(raiz) {
        raiz.traverse((o) => { if (o.isMesh) o.frustumCulled = false })
        renderer.render(scene, camera)
        raiz.traverse((o) => { if (o.isMesh) o.frustumCulled = true })
    }

    /**
     * ENTRADA CINEMATOGRÁFICA: preloader + revelación.
     * El gestor de carga alimenta el % del preloader; cuando todo cargó (o tras
     * un tope de seguridad), revelamos: se desvanece el preloader, la cámara
     * hace dolly-in y el contenido del hero aparece. Resiliente: siempre revela.
     */
    const preloader = document.getElementById('preloader')
    const preBarra = document.getElementById('preloader-barra')
    const prePct = document.getElementById('preloader-pct')

    let escenaRevelada = false
    let revelarTimeout = null

    function revelarEscena() {
        if (escenaRevelada) return
        escenaRevelada = true
        clearTimeout(revelarTimeout)

        if (preloader) {
            gsap.to(preloader, {
                opacity: 0, duration: 0.8, ease: 'power2.out',
                onComplete: () => { preloader.style.display = 'none' },
            })
        }
        document.body.classList.add('escena-lista')

        if (movimientoReducido) {
            camera.position.z = 6 // sin animación: deja la cámara en su sitio
            return
        }

        // Dolly-in de cámara: la escena "entra".
        gsap.to(camera.position, { z: 6, duration: 1.8, ease: 'power2.out' })

        // El contenido del hero (primer bloque) aparece desde abajo.
        const heroContenido = document.querySelector('.section .section__contenido')
        if (heroContenido) {
            gsap.from(heroContenido, { opacity: 0, y: 30, duration: 1, delay: 0.25, ease: 'power2.out' })
        }
    }

    // Ritmo del preloader: lo dejamos un poco pausado a propósito (se siente más
    // elegante). La barra se SUAVIZA (los modelos cargan a saltos) y garantizamos
    // un tiempo mínimo en pantalla; pero si la carga real tarda más, manda ella.
    const tInicioCarga = performance.now()
    const TIEMPO_MIN_PRELOADER = 1800 // ms mínimos visible (sube/baja para el "feel")
    const progreso = { mostrado: 0 }

    const pintarProgreso = () => {
        const v = Math.round(progreso.mostrado)
        if (preBarra) preBarra.style.width = v + '%'
        if (prePct) prePct.textContent = v + '%'
    }

    manager.onProgress = (url, cargados, total) => {
        const pct = total ? (cargados / total) * 100 : 0
        gsap.to(progreso, { mostrado: pct, duration: 0.6, ease: 'power1.out', onUpdate: pintarProgreso })
    }

    manager.onLoad = () => {
        // Lleva la barra a 100 ocupando el tiempo mínimo que falte; luego revela.
        const espera = Math.max(0, TIEMPO_MIN_PRELOADER - (performance.now() - tInicioCarga))
        gsap.to(progreso, {
            mostrado: 100, duration: Math.max(0.4, espera / 1000), ease: 'power1.inOut',
            onUpdate: pintarProgreso, onComplete: revelarEscena,
        })
    }
    // Red de seguridad: si algún asset se atora, revela igual a los 8s.
    revelarTimeout = setTimeout(revelarEscena, 8000)

    /**
     * Eventos. Usamos funciones NOMBRADAS para poder quitarlas en destroy().
     */
    function onResize() {
        // Móvil: al hacer scroll, la barra del navegador se oculta/muestra y cambia
        // el ALTO de la ventana → dispara resize → el canvas se re-dimensiona y
        // "salta". Ignoramos los cambios de solo-alto; solo reaccionamos al ANCHO
        // (rotación de pantalla). En escritorio siempre se redimensiona normal.
        if (config.movil && window.innerWidth === sizes.width) return

        sizes.width = window.innerWidth
        sizes.height = window.innerHeight
        camera.aspect = sizes.width / sizes.height
        camera.updateProjectionMatrix()
        renderer.setSize(sizes.width, sizes.height)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, config.pixelRatioMax))
        if (composer) {
            composer.setSize(sizes.width, sizes.height)
            composer.setPixelRatio(Math.min(window.devicePixelRatio, config.pixelRatioMax))
        }
    }
    window.addEventListener('resize', onResize)

    let scrollY = window.scrollY
    let currentSection = 0
    function onScroll() {
        scrollY = window.scrollY
        const newSection = Math.round(scrollY / sizes.height)
        if (newSection !== currentSection && sectionMeshes[newSection]) {
            currentSection = newSection
            if (movimientoReducido) return // sin flourish si pidieron menos movimiento

            const meshActual = sectionMeshes[currentSection]
            if (meshActual.userData.esModelo) {
                // Vuelta completa que regresa al frente.
                gsap.to(meshActual.rotation, {
                    duration: 1.5, ease: 'power2.inOut', y: meshActual.rotation.y + Math.PI * 2,
                })
            } else if (!meshActual.userData.esTexto) {
                // Primitivas: tumbo.
                gsap.to(meshActual.rotation, {
                    duration: 1.5, ease: 'power2.inOut', x: '+=6', y: '+=3', z: '+=1.5',
                })
            }
        }
    }
    window.addEventListener('scroll', onScroll)

    const cursor = { x: 0, y: 0 }
    function onMouseMove(event) {
        cursor.x = event.clientX / sizes.width - 0.5
        cursor.y = event.clientY / sizes.height - 0.5
    }
    window.addEventListener('mousemove', onMouseMove)

    /**
     * Bucle de render.
     */
    const clock = new THREE.Clock()
    let previousTime = 0
    let rafId = null

    function tick() {
        const elapsedTime = clock.getElapsedTime()
        // Clamp: si vuelves de otra pestaña, evita un salto enorme de tiempo.
        const deltaTime = Math.min(elapsedTime - previousTime, 0.1)
        previousTime = elapsedTime

        // Cámara sigue el scroll (es la base del layout: siempre activa).
        camera.position.y = - scrollY / sizes.height * objectsDistance

        // Animaciones automáticas: se omiten si el usuario pidió menos movimiento.
        if (!movimientoReducido) {
            // Parallax con el cursor
            cameraGroup.position.x += (cursor.x - cameraGroup.position.x) * 5 * deltaTime
            cameraGroup.position.y += (- cursor.y - cameraGroup.position.y) * 5 * deltaTime

            // Figuras del scroll
            for (const mesh of sectionMeshes) {
                if (!mesh || mesh.userData.esTexto) continue

                if (mesh.userData.esModelo) {
                    // Flotando en el espacio (mira al frente; el giro lo hace el scroll).
                    const t = elapsedTime + mesh.userData.flotaOffset
                    mesh.position.y = mesh.userData.baseY + Math.sin(t * 0.8) * 0.15
                    mesh.rotation.x = Math.sin(t * 0.5) * 0.08
                    mesh.rotation.z = Math.cos(t * 0.4) * 0.06
                    continue
                }

                // Primitivas: giro continuo.
                mesh.rotation.x += deltaTime * 0.1
                mesh.rotation.y += deltaTime * 0.12
            }

            // Flotación de los títulos 3D (no giran: deben leerse).
            for (const titulo of titulos3D) {
                const t = elapsedTime + titulo.userData.flotaOffset
                titulo.position.y = titulo.userData.baseY + Math.sin(t * 0.8) * 0.1
            }
        }

        if (composer) composer.render()
        else renderer.render(scene, camera)
        rafId = window.requestAnimationFrame(tick)
    }
    tick()

    // Rendimiento: pausa el loop cuando la pestaña no está visible (ahorra batería/CPU).
    function onVisibility() {
        if (document.hidden) {
            if (rafId) { window.cancelAnimationFrame(rafId); rafId = null }
        } else if (rafId === null) {
            previousTime = clock.getElapsedTime() // re-sincroniza para no dar un salto
            tick()
        }
    }
    document.addEventListener('visibilitychange', onVisibility)

    /**
     * Controlador para limpiar la escena (detener el loop y quitar listeners).
     * Útil si algún día embebes esto en una app que navega sin recargar.
     */
    function destroy() {
        if (rafId) window.cancelAnimationFrame(rafId)
        clearTimeout(revelarTimeout)
        window.removeEventListener('resize', onResize)
        window.removeEventListener('scroll', onScroll)
        window.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('visibilitychange', onVisibility)
        if (gui) gui.destroy()
        if (composer) composer.dispose()
        renderer.dispose()
    }

    return { destroy }
}
